import { getAuthItem, getAuthJson, setAuthItem } from "./authStorage";

const STORAGE_KEY = "hrmsNavbarNotifications";
const RBAC_STORAGE_KEY = "rbacSnapshot";
const MAX_NOTIFICATIONS = 80;

/** Workflow role keys used for attendance schedule notifications. */
export function getCurrentUserNotificationRoles() {
  const roles = new Set();
  try {
    const user = getAuthJson("currentUser");
    const rawRole = String(user?.role || user?.userType || "").toLowerCase();
    if (rawRole === "admin" || rawRole === "super_admin") roles.add("admin");
    if (rawRole === "super_admin") roles.add("management");
    const custom = String(user?.customRoleCode || user?.roleCode || "").toLowerCase();
    if (custom.includes("hr")) roles.add("hr");
    if (custom.includes("manager")) roles.add("manager");
  } catch {
    /* ignore */
  }
  try {
    const snap = getAuthJson(RBAC_STORAGE_KEY);
    const code = String(snap?.roleCode || "").toLowerCase();
    const name = String(snap?.roleName || "").toLowerCase();
    if (code.includes("admin") || snap?.role === "admin" || snap?.role === "super_admin") {
      roles.add("admin");
    }
    if (code.includes("hr") || name.includes("hr")) roles.add("hr");
    if (code.includes("manager") || name.includes("manager")) roles.add("manager");
    if (code.includes("management") || name.includes("management")) roles.add("management");
  } catch {
    /* ignore */
  }
  return [...roles];
}

function notificationVisibleToCurrentUser(n, userId) {
  const targetRoles = Array.isArray(n?.targetRoles) ? n.targetRoles : [];
  if (targetRoles.length) {
    const userRoles = getCurrentUserNotificationRoles();
    return targetRoles.some((r) => userRoles.includes(r));
  }
  if (n.userId != null) return String(n.userId) === String(userId);
  return true;
}

export function loadNavbarNotifications() {
  try {
    const raw = getAuthItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveNavbarNotifications(items) {
  try {
    setAuthItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent("hrms:navbar-notification"));
  } catch {
    /* ignore */
  }
}

export function getCurrentUserId() {
  try {
    const user = getAuthJson("currentUser");
    return user?.id ?? user?.userId ?? null;
  } catch {
    return null;
  }
}

/** Notifications for the logged-in user (broadcast, role-targeted, or user-specific). */
export function getNotificationsForCurrentUser() {
  const userId = getCurrentUserId();
  return loadNavbarNotifications().filter((n) =>
    notificationVisibleToCurrentUser(n, userId)
  );
}

export function addNavbarNotification({
  userId = null,
  targetRoles = null,
  title,
  message,
  type = "info",
  sourceId = null,
}) {
  const entry = {
    id: `n_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    userId: userId != null ? userId : null,
    targetRoles: Array.isArray(targetRoles) && targetRoles.length ? targetRoles : null,
    title: title || "Notification",
    message: message || "",
    type,
    sourceId: sourceId || null,
    createdAt: Date.now(),
  };
  const next = [entry, ...loadNavbarNotifications()].slice(0, MAX_NOTIFICATIONS);
  saveNavbarNotifications(next);
  return entry;
}

export function dismissNavbarNotification(id) {
  const next = loadNavbarNotifications().filter((n) => n.id !== id);
  saveNavbarNotifications(next);
  return next;
}

export function clearNavbarNotificationsForCurrentUser() {
  const userId = getCurrentUserId();
  const next = loadNavbarNotifications().filter(
    (n) => !notificationVisibleToCurrentUser(n, userId)
  );
  saveNavbarNotifications(next);
  return next;
}

export function formatLeaveStatusNotification(row, status) {
  const fd = row?.formData || {};
  const name = row?.employeeName || row?.employeeCode || "Employee";
  const code = row?.employeeCode ? ` (${row.employeeCode})` : "";
  const type = fd.leaveType || "Leave";
  const duration =
    fd.leaveDuration === "first_half"
      ? "First half"
      : fd.leaveDuration === "second_half"
      ? "Second half"
      : "Full day";
  const dates =
    fd.fromDate && fd.toDate
      ? `${fd.fromDate} → ${fd.toDate}`
      : fd.fromDate || fd.toDate || "";

  const detail = [type, duration, dates].filter(Boolean).join(" · ");

  if (status === "approved") {
    return {
      title: "Leave approved",
      message: `${name}${code}: ${detail} has been approved.`,
      type: "leave_approved",
    };
  }
  if (status === "rejected") {
    return {
      title: "Leave rejected",
      message: `${name}${code}: ${detail} has been rejected.`,
      type: "leave_rejected",
    };
  }
  return {
    title: "Leave update",
    message: `${name}${code}: ${detail} — ${status}.`,
    type: "leave_update",
  };
}

export function notifyLeaveStatusChange(row, status, { targetUserId = null } = {}) {
  const { title, message, type } = formatLeaveStatusNotification(row, status);
  return addNavbarNotification({
    userId: targetUserId,
    title,
    message,
    type,
  });
}

export function formatRestrictedHolidayStatusNotification(row, status) {
  const fd = row?.formData || {};
  const holiday =
    fd.holidayName || row?.holidayName || "Restricted Holiday";
  const date = String(fd.date || fd.holidayDate || row?.holidayDate || "").slice(0, 10);
  const detail = [holiday, date].filter(Boolean).join(" · ");
  if (status === "approved") {
    return {
      title: "Restricted Holiday approved",
      message: `Your Restricted Holiday (${detail}) was approved.`,
      type: "rh_approved",
    };
  }
  if (status === "rejected") {
    return {
      title: "Restricted Holiday rejected",
      message: `Your Restricted Holiday (${detail}) was rejected.`,
      type: "rh_rejected",
    };
  }
  return {
    title: "Restricted Holiday update",
    message: `Your Restricted Holiday (${detail}) — ${status}.`,
    type: "rh_update",
  };
}

/** Local bell fallback (server notify is primary via updateRestrictedHolidayStatus). */
export function notifyRestrictedHolidayStatusChange(
  row,
  status,
  { targetUserId = null } = {}
) {
  const { title, message, type } = formatRestrictedHolidayStatusNotification(
    row,
    status
  );
  return addNavbarNotification({
    userId: targetUserId,
    title,
    message,
    type,
    sourceId: row?.requestId ? `rh_${row.requestId}_${status}` : null,
  });
}
